import React, { Fragment, useRef } from "react";

import { Typography, Box, Divider, CircularProgress } from "@mui/material";

import { TripCategory } from "types";
import { CreateTripState } from "pages/new/context/initialState";
import VehicleCard from "./components/VehicleCard";
import VehicleCategoryCard from "./components/VehicleCategoryCard";
import useLoadVehicles from "./hooks/useLoadVehicles";
import useVehicleFilters from "./hooks/useVehicleFilters";
import { useScreenSize, useCurrentUser } from "globals/hooks";
import { useLaunchDarklyFlags } from "globals/hooks/useLaunchDarklyFlags";
import {
  FilterButton,
  FilterDropdown,
  ActiveFilterChips,
} from "./components/VehicleFilters";
import useLoadVehicleCategories from "./hooks/useLoadVehicleCategories";

type VehicleListProps = {
  trip: CreateTripState;
  tripCategory: TripCategory;
  vehicleId: string;
  onVehicleSelect: (vehicle: any) => () => void;
  returnTrip?: CreateTripState;
};

function VehicleList(props: VehicleListProps) {
  const { trip, returnTrip, onVehicleSelect, tripCategory } = props;
  const filterButtonRef = useRef<HTMLButtonElement>(null);

  // hooks
  const { isMobileView } = useScreenSize();
  const { enableVehicleCategory } = useLaunchDarklyFlags();
  const currentUser = useCurrentUser();

  const { vehicles, isLoadingVehicles, isLoadingBRA } = useLoadVehicles({
    trip,
    returnTrip,
    tripCategory,
    ignoreVehicleCategory: enableVehicleCategory,
  });

  // Load vehicle categories from the server only if feature flag is enabled
  const vehicleCategoriesData = useLoadVehicleCategories({
    trip,
    returnTrip,
    enabled: enableVehicleCategory,
    authenticatedUser: currentUser,
  });

  const {
    isFilterOpen,
    filters,
    hasActiveFilters,
    activeFilterCount,
    handleFilterOpen,
    handleFilterClose,
    updateFilter,
    clearFilter,
    filterVehicles,
  } = useVehicleFilters({
    defaultMinCapacity: trip.totalGroupSize,
  });

  // Apply filters to vehicles with safety check
  const filteredVehicles = filterVehicles(vehicles || []);

  // Apply minCapacity filter to vehicle categories
  const filteredVehicleCategories =
    vehicleCategoriesData.vehicleCategories.filter((category) => {
      if (filters.minCapacity !== null && category.capacity < filters.minCapacity) {
        return false;
      }
      return true;
    });

  return (
    <>
      <Box mt={5} mb={2} mx={isMobileView ? 0.5 : 0}>
        {isMobileView ? (
          <>
            {/* Mobile Layout */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h3">Choose Vehicle</Typography>
              <FilterButton
                ref={filterButtonRef}
                onClick={handleFilterOpen}
                filterCount={activeFilterCount}
              />
            </Box>
            {hasActiveFilters && (
              <Box mt={1}>
                <ActiveFilterChips
                  filters={filters}
                  onRemoveFilter={clearFilter}
                />
              </Box>
            )}
          </>
        ) : (
          /* Desktop Layout */
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h3" sx={{ flexShrink: 0 }}>
              Choose Vehicle
            </Typography>
            <Box
              sx={{ flexGrow: 1, display: "flex", justifyContent: "flex-end" }}
            >
              {hasActiveFilters && (
                <ActiveFilterChips
                  filters={filters}
                  onRemoveFilter={clearFilter}
                />
              )}
            </Box>
            <FilterButton
              ref={filterButtonRef}
              onClick={handleFilterOpen}
              filterCount={activeFilterCount}
            />
          </Box>
        )}
      </Box>
      {isLoadingVehicles && vehicles.length === 0 && (
        <>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Box mb={2}>Loading vehicles...</Box>
            <Box mb={2}>
              <CircularProgress />
            </Box>
          </Box>
          <Divider />
        </>
      )}

      {/* Display Vehicle Categories */}
      {!vehicleCategoriesData.isLoadingVehicleCategories &&
        filteredVehicleCategories.length > 0 && (
          <>
            {filteredVehicleCategories.map((category) => {
              const isUnavailable =
                !!category.hasTripConflict || !!category.hasUnavailabilityBlock;

              // Create a minimal vehicle-like object with category reference
              const vehicleCategoryObject = {
                id: category.id,
                name: category.name,
                capacity: category.capacity,
                operator: category.operator,
                photos:
                  category.photos?.map((photo, idx) => ({
                    id: `${category.id}-photo-${idx}`,
                    url: photo.url,
                    photoIndex: photo.index || idx,
                  })) || [],
                features: category.features || [],
                baseRateAutomation: category.baseRateAutomation,
                returnBaseRateAutomation: category.returnBaseRateAutomation,
                hasTripConflict: category.hasTripConflict,
                hasUnavailabilityBlock: category.hasUnavailabilityBlock,
                vehicleType: {
                  typeName: "Category",
                  typeSlug: "category",
                },
                settings: category.customerPortalSettings
                  ? {
                      id: category.id,
                      pricelessBookingEnabled:
                        !category.enableBaseRateAutomation,
                      conflictBlockQuote:
                        category.customerPortalSettings.conflictBlockQuote,
                      conflictBlockReservation:
                        category.customerPortalSettings
                          .conflictBlockReservation,
                    }
                  : null,
                // This is the key field that sanitizeRequestForCreateMutation checks
                category: category,
              };

              const handleCategorySelect = () => {
                if (isUnavailable) return;
                onVehicleSelect(vehicleCategoryObject)();
              };

              return (
                <Fragment key={category.id}>
                  <VehicleCategoryCard
                    category={category}
                    onCategorySelect={handleCategorySelect}
                  />
                  <Box mt={3}>
                    <Divider />
                  </Box>
                </Fragment>
              );
            })}
          </>
        )}

      {/* Original Vehicle Cards */}
      {filteredVehicles.map((vehicle) => {
        return (
          <Fragment key={vehicle.id}>
            <VehicleCard
              isLoadingBRA={isLoadingBRA}
              onVehicleSelect={onVehicleSelect(vehicle)}
              vehicle={vehicle}
            />
            <Box mt={3}>
              <Divider />
            </Box>
          </Fragment>
        );
      })}

      <FilterDropdown
        isOpen={isFilterOpen}
        onClose={handleFilterClose}
        anchorEl={filterButtonRef.current}
        filters={filters}
        onFilterChange={updateFilter}
        passengerCount={trip.totalGroupSize}
      />
    </>
  );
}

export default VehicleList;
